import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcInventoryV1FirmOrderQueryServiceFirmOrderGet } from '@interxfi/api-client/generated/hooks/ixc-inventory-v1-firm-order-query-service-firm-order-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcInventoryV1FirmOrderState } from '@interxfi/types/generated/api';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { InventoryFirmOrderEntityMenu } from '../../../../../../../../../../features/inventory/firm-order/inventory-firm-order-entity-menu';
import { InventoryFirmOrderEventSummary } from '../../../../../../../../../../features/inventory/firm-order/inventory-firm-order-event';
import { FirmOrderFillsTable } from '../../../../../../../../../../features/inventory/firm-order/inventory-firm-order-fills-table';
import { getInventoryFirmOrderStateListItems } from '../../../../../../../../../../features/inventory/firm-order/inventory-firm-order-list-items';
import { getInventoryFirmOrderDetailPath } from '../../../../../../../../../../features/inventory/firm-order/inventory-firm-order-routing';
import { InventoryFirmOrderStatusPill } from '../../../../../../../../../../features/inventory/firm-order/inventory-firm-order-status-pill';
import { InventoryFirmOrderSummaryBar } from '../../../../../../../../../../features/inventory/firm-order/inventory-firm-order-summary-bar';
import { OrderSidePill } from '../../../../../../../../../../features/order/order-side-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/inventory/order/_order/$firmOrderId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `inventory-firm-order-${params.firmOrderId}`,
        label: 'Firm Order',
        to: getInventoryFirmOrderDetailPath(params.firmOrderId),
        suffix: (
          <CopyButton
            content={params.firmOrderId}
            label="Copy Firm Order ID"
            tooltip={{ copy: params.firmOrderId, copied: 'Copied Firm Order ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Firm Order'),
  component: FirmOrderDetail,
});

function getListItems(data: IxcInventoryV1FirmOrderState | undefined) {
  const listItems = getInventoryFirmOrderStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [
            listItems.data.kind,
            ...(listItems.data.asset.summary ? [listItems.data.asset.summary] : []),
            listItems.data.instruction.sidePill,
            listItems.data.instruction.quantity,
            listItems.data.instruction.typeName,
            ...(listItems.data.instruction.limitPrice ? [listItems.data.instruction.limitPrice] : []),
            listItems.data.instruction.timeInForce,
            ...(listItems.data.customerOrder ? [listItems.data.customerOrder.capacity] : []),
          ],
        },
        {
          id: 'execution',
          title: 'Execution',
          items: [listItems.data.totalQuantity, listItems.data.averagePrice, listItems.data.totalPrice],
        },
        ...(listItems.data.canceled
          ? [{ id: 'canceled', title: 'Canceled', items: [listItems.data.canceled.cumulativeQuantity, listItems.data.canceled.solicited] }]
          : []),
      ] satisfies ReadonlyListGroup[],
      right: [
        listItems.metadata.createdAt,
        listItems.metadata.updatedAt,
        listItems.firmOrderId,
        listItems.data.fixOrderId,
        ...(listItems.data.parentBooksOrderId ? [listItems.data.parentBooksOrderId] : []),
        ...(listItems.data.customerOrder
          ? [{ ...listItems.data.customerOrder.orderId, title: 'Customer Order ID' }, listItems.data.customerOrder.accountId]
          : []),
      ] satisfies ReadonlyListItem[],
    },
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load firm order', 'view this firm order');

function FirmOrderDetail() {
  const { firmOrderId } = Route.useParams();
  const request = { firmOrderId } as const;
  const { data, error, isPending } = useIxcInventoryV1FirmOrderQueryServiceFirmOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.firmOrder), [data?.firmOrder]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <InventoryFirmOrderStatusPill isPending={showPending} size="large" status={data?.firmOrder?.status} />
            <OrderSidePill isPending={showPending} side={data?.firmOrder?.data?.instruction?.side} size="large" />
          </HorizontalStack>

          <InventoryFirmOrderEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <InventoryFirmOrderSummaryBar {...request} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <InventoryFirmOrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>General</Typography.Level3>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Fills</Typography.Level3>
          <FirmOrderFillsTable fills={data?.firmOrder?.data?.fills ?? STABLE_EMPTY_ARRAY_REF} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
